import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthShell } from "@/components/AuthShell";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function LoginPage({ portal = "customer" }) {
  const isSuper = portal === "super";
  const { user } = useAuth();
  const [params] = useSearchParams();
  const kunde = params.get("kunde");
  const [email, setEmail] = useState(params.get("e") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(null);
  const err = params.get("feil") || "";

  useEffect(() => {
    const reset = () => setBusy(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  useEffect(() => {
    if (isSuper || !kunde) return;
    api.get(`/public/branding/${encodeURIComponent(kunde)}`).then((r) => setBrand(r.data.logo ? r.data : null)).catch(() => {});
  }, [isSuper, kunde]);

  if (user && (isSuper ? user.role === "super_admin" : user.role === "customer_admin")) {
    return <Navigate to={isSuper ? "/superadmin" : `/admin/${user.customer_id}`} replace />;
  }

  const submit = (e) => {
    if (busy) e.preventDefault();
    else setBusy(true);
  };

  return (
    <AuthShell isSuper={isSuper} logo={brand?.logo} logoAlt={brand?.name}>
      <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-900" data-testid="login-title">
        {isSuper ? "Super Admin" : "Logg inn"}
      </h2>
      <p className="mt-2 text-sm text-slate-500">
        {isSuper ? "Kun for plattformadministratorer." : "Logg inn for å redigere havnekalkulatoren din."}
      </p>
      <form onSubmit={submit} method="post" action="/api/auth/login-form" autoComplete="on" className="mt-8 space-y-5" name="login" data-testid="login-form">
        <input type="hidden" name="portal" value={portal} />
        <input type="hidden" name="remember" value={remember ? "1" : "0"} />
        {kunde && <input type="hidden" name="kunde" value={kunde} />}
        <div className="space-y-2">
          <Label htmlFor="email">E-post</Label>
          <Input id="email" name="email" type="email" autoComplete="username" required value={email}
            onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Passord</Label>
            <Link to="/glemt-passord" className="text-sm text-sky-700 hover:underline" data-testid="forgot-password-link">
              Glemt passord?
            </Link>
          </div>
          <PasswordInput id="password" name="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} testId="login-password-input" />
        </div>
        <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer select-none">
          <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember-checkbox" />
          Husk meg på denne enheten
        </label>
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
            {err}
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full h-11 text-base" data-testid="login-submit-button">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Logg inn
        </Button>
      </form>
    </AuthShell>
  );
}
